import { formatLocalDateTime, formatNumber, parseUtcInstant } from "./lidarTelemetryFields";

function parseNestedMpptFields(row) {
  const mppt =
    row?.mppt ?? row?.payload?.mppt ?? row?.payload?.data?.mppt ?? null;

  if (!mppt || typeof mppt !== "object") {
    return {};
  }

  const panel = mppt.panel_data ?? mppt.panelData ?? {};
  const battery = mppt.battery_data ?? mppt.batteryData ?? {};
  const load = mppt.load_data ?? mppt.loadData ?? {};
  const status = mppt.status ?? {};

  return {
    mpptPanelVoltage: panel.voltage ?? null,
    mpptPanelCurrent: panel.current ?? null,
    mpptPanelPower: panel.power ?? null,
    mpptBatteryVoltage: battery.voltage ?? null,
    mpptBatteryCurrent: battery.current ?? null,
    mpptBatteryPower: battery.power ?? null,
    mpptBatteryPercentage: battery.percentage ?? null,
    mpptLoadVoltage: load.voltage ?? null,
    mpptLoadCurrent: load.current ?? null,
    mpptLoadPower: load.power ?? null,
    mpptLoadStatus: load.status ?? null,
    mpptLoadBrightness: load.brightness ?? null,
    mpptChargingStatus: status.charging_status ?? status.chargingStatus ?? null,
    mpptFaultCode: status.fault_code ?? status.faultCode ?? null,
  };
}

function parseNestedRainfallFields(row) {
  const rainfall =
    row?.rainfall ?? row?.payload?.rainfall ?? row?.payload?.data?.rainfall ?? null;

  if (!rainfall || typeof rainfall !== "object") {
    return {};
  }

  return {
    rainfallIntensityMmPerMin:
      rainfall.intensity_mm_per_min ?? rainfall.intensityMmPerMin ?? null,
    rainfallCumulativeMm: rainfall.cumulative_mm ?? rainfall.cumulativeMm ?? null,
  };
}

function parseNestedLidarFields(row) {
  const lidar =
    row?.lidar ??
    row?.payload?.lidar ??
    row?.payload?.data?.lidar ??
    null;
  const parentBlinker =
    row?.blinker && typeof row.blinker === "object" ? row.blinker : {};
  const lidarBlinker =
    lidar?.blinker && typeof lidar.blinker === "object" ? lidar.blinker : {};
  const blinker = { ...parentBlinker, ...lidarBlinker };

  if (!lidar || typeof lidar !== "object") {
    return {
      lidarDistanceCm: null,
      lidarLevelCm: null,
      confirmedDistAvgCm: null,
      waterLevelAvgCm: null,
      confirmedLevelAvgCm: null,
      rawDistanceCm: null,
      strength: null,
      confidencePct: null,
      referenceHeightCm: null,
      dataValid: null,
      durationSec: null,
      startTime: null,
      endTime: null,
      blinkerActive: null,
      blinkerOnThresholdCm: null,
      blinkerOffThresholdCm: null,
    };
  }

  return {
    lidarDistanceCm:
      lidar.distance_cm ??
      lidar.distanceCm ??
      lidar.confirmed_dist_avg_cm ??
      lidar.confirmedDistAvgCm ??
      null,
    lidarLevelCm:
      lidar.level_cm ??
      lidar.levelCm ??
      lidar.confirmed_level_avg_cm ??
      lidar.confirmedLevelAvgCm ??
      null,
    confirmedDistAvgCm:
      lidar.confirmed_dist_avg_cm ?? lidar.confirmedDistAvgCm ?? null,
    waterLevelAvgCm:
      lidar.water_level_avg_cm ?? lidar.waterLevelAvgCm ?? null,
    confirmedLevelAvgCm:
      lidar.confirmed_level_avg_cm ??
      lidar.confirmedLevelAvgCm ??
      lidar.level_cm ??
      lidar.levelCm ??
      null,
    rawDistanceCm: lidar.raw_distance_cm ?? lidar.rawDistanceCm ?? null,
    strength: lidar.strength ?? null,
    confidencePct: lidar.confidence_pct ?? lidar.confidencePct ?? null,
    referenceHeightCm:
      lidar.reference_height_cm ?? lidar.referenceHeightCm ?? null,
    dataValid: lidar.data_valid ?? lidar.dataValid ?? null,
    durationSec: lidar.duration_sec ?? lidar.durationSec ?? null,
    startTime: lidar.start_time ?? lidar.startTime ?? null,
    endTime: lidar.end_time ?? lidar.endTime ?? null,
    blinkerActive:
      lidar.active ?? lidar.blinker_active ?? blinker.active ?? null,
    blinkerOnThresholdCm:
      lidar.on_threshold_cm ??
      lidar.onThresholdCm ??
      lidar.blinker_on_threshold_cm ??
      blinker.on_threshold_cm ??
      blinker.onThresholdCm ??
      null,
    blinkerOffThresholdCm:
      lidar.off_threshold_cm ??
      lidar.offThresholdCm ??
      lidar.blinker_off_threshold_cm ??
      blinker.off_threshold_cm ??
      blinker.offThresholdCm ??
      null,
  };
}

/** Map legacy Redis/socket keys (panelVoltage) to current mppt_* fields. */
function normalizeSensorLivePayload(row) {
  if (!row || typeof row !== "object") return {};

  const nestedLidar = parseNestedLidarFields(row);
  const nestedMppt = parseNestedMpptFields(row);
  const nestedRainfall = parseNestedRainfallFields(row);

  return {
    ...row,
    deviceRole: row.deviceRole ?? row.device_role ?? null,
    receivedAt:
      row.receivedAt ?? row.timestamp ?? row.payload?.timestamp ?? null,
    lidarDistanceCm:
      row.lidarDistanceCm ??
      row.lidar_distance_cm ??
      nestedLidar.lidarDistanceCm ??
      null,
    lidarLevelCm:
      row.lidarLevelCm ??
      row.lidar_level_cm ??
      nestedLidar.lidarLevelCm ??
      nestedLidar.confirmedLevelAvgCm ??
      null,
    confirmedDistAvgCm:
      row.confirmedDistAvgCm ??
      row.confirmed_dist_avg_cm ??
      nestedLidar.confirmedDistAvgCm ??
      null,
    waterLevelAvgCm:
      row.waterLevelAvgCm ??
      row.water_level_avg_cm ??
      nestedLidar.waterLevelAvgCm ??
      null,
    confirmedLevelAvgCm:
      row.confirmedLevelAvgCm ??
      row.confirmed_level_avg_cm ??
      nestedLidar.confirmedLevelAvgCm ??
      nestedLidar.lidarLevelCm ??
      null,
    rawDistanceCm:
      row.rawDistanceCm ?? row.raw_distance_cm ?? nestedLidar.rawDistanceCm ?? null,
    strength: row.strength ?? nestedLidar.strength ?? null,
    confidencePct:
      row.confidencePct ??
      row.confidence_pct ??
      nestedLidar.confidencePct ??
      null,
    referenceHeightCm:
      row.referenceHeightCm ??
      row.reference_height_cm ??
      nestedLidar.referenceHeightCm ??
      null,
    dataValid:
      row.dataValid ?? row.data_valid ?? nestedLidar.dataValid ?? null,
    durationSec:
      row.durationSec ?? row.duration_sec ?? nestedLidar.durationSec ?? null,
    startTime: row.startTime ?? row.start_time ?? nestedLidar.startTime ?? null,
    endTime: row.endTime ?? row.end_time ?? nestedLidar.endTime ?? null,
    blinkerActive:
      row.blinkerActive ??
      row.blinker_active ??
      (row.blinker && typeof row.blinker === "object"
        ? row.blinker.active
        : null) ??
      nestedLidar.blinkerActive ??
      null,
    blinkerOnThresholdCm:
      row.blinkerOnThresholdCm ??
      row.blinker_on_threshold_cm ??
      (row.blinker && typeof row.blinker === "object"
        ? row.blinker.on_threshold_cm ?? row.blinker.onThresholdCm
        : null) ??
      nestedLidar.blinkerOnThresholdCm ??
      null,
    blinkerOffThresholdCm:
      row.blinkerOffThresholdCm ??
      row.blinker_off_threshold_cm ??
      (row.blinker && typeof row.blinker === "object"
        ? row.blinker.off_threshold_cm ?? row.blinker.offThresholdCm
        : null) ??
      nestedLidar.blinkerOffThresholdCm ??
      null,
    rainfallIntensityMmPerMin:
      row.rainfallIntensityMmPerMin ??
      row.rainfall_intensity_mm_per_min ??
      nestedRainfall.rainfallIntensityMmPerMin ??
      null,
    rainfallCumulativeMm:
      row.rainfallCumulativeMm ??
      row.rainfall_cumulative_mm ??
      nestedRainfall.rainfallCumulativeMm ??
      null,
    mpptPanelVoltage:
      row.mpptPanelVoltage ??
      row.panelVoltage ??
      row.mppt_panel_voltage ??
      nestedMppt.mpptPanelVoltage ??
      null,
    mpptPanelCurrent:
      row.mpptPanelCurrent ??
      row.panelCurrent ??
      row.mppt_panel_current ??
      nestedMppt.mpptPanelCurrent ??
      null,
    mpptPanelPower:
      row.mpptPanelPower ??
      row.panelPower ??
      row.mppt_panel_power ??
      nestedMppt.mpptPanelPower ??
      null,
    mpptBatteryVoltage:
      row.mpptBatteryVoltage ??
      row.batteryVoltage ??
      row.mppt_battery_voltage ??
      nestedMppt.mpptBatteryVoltage ??
      null,
    mpptBatteryCurrent:
      row.mpptBatteryCurrent ??
      row.batteryCurrent ??
      row.mppt_battery_current ??
      nestedMppt.mpptBatteryCurrent ??
      null,
    mpptBatteryPower:
      row.mpptBatteryPower ??
      row.batteryPower ??
      row.mppt_battery_power ??
      nestedMppt.mpptBatteryPower ??
      null,
    mpptBatteryPercentage:
      row.mpptBatteryPercentage ??
      row.batteryPercentage ??
      row.mppt_battery_percentage ??
      nestedMppt.mpptBatteryPercentage ??
      null,
    mpptLoadVoltage:
      row.mpptLoadVoltage ??
      row.loadVoltage ??
      row.mppt_load_voltage ??
      nestedMppt.mpptLoadVoltage ??
      null,
    mpptLoadCurrent:
      row.mpptLoadCurrent ??
      row.loadCurrent ??
      row.mppt_load_current ??
      nestedMppt.mpptLoadCurrent ??
      null,
    mpptLoadPower:
      row.mpptLoadPower ??
      row.loadPower ??
      row.mppt_load_power ??
      nestedMppt.mpptLoadPower ??
      null,
    mpptLoadStatus:
      row.mpptLoadStatus ??
      row.loadStatus ??
      row.mppt_load_status ??
      nestedMppt.mpptLoadStatus ??
      null,
    mpptLoadBrightness:
      row.mpptLoadBrightness ??
      row.loadBrightness ??
      row.mppt_load_brightness ??
      nestedMppt.mpptLoadBrightness ??
      null,
    mpptChargingStatus:
      row.mpptChargingStatus ??
      row.chargingStatus ??
      row.mppt_charging_status ??
      nestedMppt.mpptChargingStatus ??
      null,
    mpptFaultCode:
      row.mpptFaultCode ??
      row.faultCode ??
      row.mppt_fault_code ??
      nestedMppt.mpptFaultCode ??
      null,
  };
}

export function resolveSensorTelemetryDisplay(row) {
  const normalized = normalizeSensorLivePayload(row);

  return {
    deviceId: normalized.deviceId,
    deviceRole: normalized.deviceRole,
    topic: normalized.topic,
    receivedAt: normalized.receivedAt,
    lidarDistanceCm: normalized.lidarDistanceCm,
    lidarLevelCm: normalized.lidarLevelCm,
    confirmedDistAvgCm: normalized.confirmedDistAvgCm,
    waterLevelAvgCm: normalized.waterLevelAvgCm,
    confirmedLevelAvgCm: normalized.confirmedLevelAvgCm,
    rawDistanceCm: normalized.rawDistanceCm,
    strength: normalized.strength,
    confidencePct: normalized.confidencePct,
    referenceHeightCm: normalized.referenceHeightCm,
    dataValid: normalized.dataValid,
    durationSec: normalized.durationSec,
    startTime: normalized.startTime,
    endTime: normalized.endTime,
    blinkerActive: normalized.blinkerActive,
    blinkerOnThresholdCm: normalized.blinkerOnThresholdCm,
    blinkerOffThresholdCm: normalized.blinkerOffThresholdCm,
    rainfallIntensityMmPerMin: normalized.rainfallIntensityMmPerMin,
    rainfallCumulativeMm: normalized.rainfallCumulativeMm,
    mpptPanelVoltage: normalized.mpptPanelVoltage,
    mpptPanelCurrent: normalized.mpptPanelCurrent,
    mpptPanelPower: normalized.mpptPanelPower,
    mpptBatteryVoltage: normalized.mpptBatteryVoltage,
    mpptBatteryCurrent: normalized.mpptBatteryCurrent,
    mpptBatteryPower: normalized.mpptBatteryPower,
    mpptBatteryPercentage: normalized.mpptBatteryPercentage,
    mpptLoadVoltage: normalized.mpptLoadVoltage,
    mpptLoadCurrent: normalized.mpptLoadCurrent,
    mpptLoadPower: normalized.mpptLoadPower,
    mpptLoadStatus: normalized.mpptLoadStatus,
    mpptLoadBrightness: normalized.mpptLoadBrightness,
    mpptChargingStatus: normalized.mpptChargingStatus,
    mpptFaultCode: normalized.mpptFaultCode,
  };
}

const SENSOR_LIDAR_FALLBACK_KEYS = [
  "startTime",
  "endTime",
  "durationSec",
  "confirmedDistAvgCm",
  "waterLevelAvgCm",
  "confirmedLevelAvgCm",
  "rawDistanceCm",
  "strength",
  "confidencePct",
  "referenceHeightCm",
  "dataValid",
  "blinkerActive",
  "blinkerOnThresholdCm",
  "blinkerOffThresholdCm",
];

function isSensorLidarValuePresent(value) {
  return value !== undefined && value !== null && value !== "";
}

const SAME_SENSOR_LIDAR_TIMESTAMP_MS = 2000;

export function sameSensorLidarTimestamp(a, b, windowMs = SAME_SENSOR_LIDAR_TIMESTAMP_MS) {
  const aMs = parseUtcInstant(a)?.valueOf();
  const bMs = parseUtcInstant(b)?.valueOf();
  if (!aMs || !bMs) return false;
  return Math.abs(aMs - bMs) <= windowMs;
}

/** Sensor packet wins; old lidar fills only missing lidar/blinker fields. */
export function overlayMissingSensorLidarFields(sensor, lidar) {
  if (!sensor || !lidar) return sensor;
  const merged = { ...sensor };
  for (const key of SENSOR_LIDAR_FALLBACK_KEYS) {
    if (
      !isSensorLidarValuePresent(sensor[key]) &&
      isSensorLidarValuePresent(lidar[key])
    ) {
      merged[key] = lidar[key];
    }
  }
  return merged;
}

export function sensorTelemetryRecordKey(row) {
  const display = resolveSensorTelemetryDisplay(row);
  if (row?.id) return String(row.id);
  return `${display.deviceId}|${display.receivedAt ?? ""}`;
}

/** Match live + API rows for the same reading (UUID differs between socket and DB). */
export function sensorTelemetryDedupeKey(row) {
  const display = resolveSensorTelemetryDisplay(row);
  const instant = parseUtcInstant(display.receivedAt);
  if (!display.deviceId || !instant) return sensorTelemetryRecordKey(row);
  return `${display.deviceId}|${instant.utc().format("YYYY-MM-DDTHH:mm")}`;
}

function toTelemetryNumber(value) {
  if (value === 0 || value === "0") return 0;
  if (value == null || value === "") return null;
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
}

/** Cumulative rainfall — `rainfall_cumulative_mm` only. */
export function getRainfallCumulativeMm(row) {
  const display =
    row?.rainfallCumulativeMm !== undefined
      ? row
      : resolveSensorTelemetryDisplay(row);
  return toTelemetryNumber(display.rainfallCumulativeMm);
}

/** Rain intensity — `rainfall_intensity_mm_per_min` only. */
export function getRainfallIntensityMmPerMin(row) {
  const display =
    row?.rainfallIntensityMmPerMin !== undefined
      ? row
      : resolveSensorTelemetryDisplay(row);
  return toTelemetryNumber(display.rainfallIntensityMmPerMin);
}

export const RAIN_ACTIVE_INTENSITY_MM_PER_MIN = 0;

function hasReadingValue(value) {
  if (value === 0 || value === "0") return true;
  return value !== undefined && value !== null && value !== "";
}

function pickFirstReadingValue(values) {
  for (const value of values) {
    if (value === 0 || value === "0") return Number(value);
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return null;
}

const LATEST_READING_META_KEYS = new Set([
  "deviceId",
  "deviceRole",
  "topic",
  "receivedAt",
]);

/** Newest row with gaps filled from recent readings (socket rows can be sparse). */
export function mergeLatestSensorReadings(rows) {
  if (!rows?.length) return null;

  const sorted = [...rows].sort(
    (a, b) =>
      (parseUtcInstant(b.receivedAt)?.valueOf() ?? 0) -
      (parseUtcInstant(a.receivedAt)?.valueOf() ?? 0),
  );

  const displays = sorted.map((row) => resolveSensorTelemetryDisplay(row));
  const merged = { ...displays[0] };

  for (const key of Object.keys(merged)) {
    if (LATEST_READING_META_KEYS.has(key)) continue;
    merged[key] = pickFirstReadingValue(displays.map((display) => display[key]));
  }

  return merged;
}

export { formatLocalDateTime, formatNumber, formatCmWithMeters } from "./lidarTelemetryFields";
